/**
 * Turn any thrown thing into a sentence a member can actually act on.
 *
 * Rule: NEVER show a member the raw text of an error. Supabase, Stripe, Postgres and the browser all
 * speak developer, not English ("Edge Function returned a non-2xx status code", "Failed to fetch",
 * "new row violates row-level security policy"). A member seeing that learns nothing and trusts us less.
 *
 * So: we recognise the handful of causes that a member can genuinely DO something about (they are
 * offline, they are signed out, they are not allowed, the thing is gone, they are going too fast, their
 * card was declined) and say so plainly. Everything else gets the caller's fallback, which should
 * describe what the member was trying to do. The real error goes to the console and to Sentry for us.
 *
 * Usage:
 *   toast.error(friendlyError(err, "We could not join that community just now. Please try again."));
 */

/**
 * The recognised-cause messages, keyed. These are the sentences we show a member when we DO know
 * what went wrong. English lives here as the source of truth; the same keys exist under the "errors"
 * namespace in src/messages/*.json so a translator can return them in the member's own language.
 * Use the useFriendlyError() hook on the client to get a language-aware version; the bare function
 * below falls back to this English when no translator is passed (server code, non-React callers).
 */
export type FriendlyErrorKey =
  | "friendlyGeneric"
  | "friendlyOffline"
  | "friendlySessionExpired"
  | "friendlyNoPermission"
  | "friendlyRateLimited"
  | "friendlyPayment"
  | "friendlyEventFull"
  | "friendlyAlreadyDone"
  | "friendlyFileTooLarge"
  | "friendlyNotFound";

export const FRIENDLY_ERROR_EN: Record<FriendlyErrorKey, string> = {
  friendlyGeneric: "Something went wrong. Please try again.",
  friendlyOffline: "You appear to be offline. Check your connection and try again.",
  friendlySessionExpired: "Your session has expired. Please sign in again.",
  friendlyNoPermission:
    "You do not have permission to do that. If you think that is wrong, get in touch with us.",
  friendlyRateLimited:
    "That is a lot of attempts in a short space of time. Please wait a moment and try again.",
  friendlyPayment: "Your payment could not be taken. Check your card details, or try a different card.",
  friendlyEventFull: "That event is full, so there are no places left.",
  friendlyAlreadyDone: "That has already been done. Refresh the page to see the latest.",
  friendlyFileTooLarge: "That file is too large. Please choose a smaller one and try again.",
  friendlyNotFound: "We could not find that. It may have been removed.",
};

/** A function that turns one of our keys into a sentence, for example next-intl's useTranslations("errors"). */
export type FriendlyErrorTranslator = (key: FriendlyErrorKey) => string;

/** The message text a caller gets when we genuinely cannot tell what went wrong. */
export const GENERIC_ERROR_MESSAGE = FRIENDLY_ERROR_EN.friendlyGeneric;

/**
 * The browser's own wording when a request never reaches the server (offline, flaky signal, blocked).
 * Kept in one place so that recognising it is not spread across forty files as a magic string.
 */
const NETWORK_ERROR_SIGNALS = [
  "failed to fetch",
  "networkerror",
  "network request failed",
  "load failed",
  "the internet connection appears to be offline",
  "err_internet_disconnected",
  "err_network",
  "fetch failed",
  "econnrefused",
  "econnreset",
  "enotfound",
  "timeout",
  "timed out",
  "aborted",
];

const AUTH_ERROR_SIGNALS = [
  "jwt expired",
  "invalid jwt",
  "refresh token",
  "not authenticated",
  "unauthenticated",
  "unauthorized",
  "auth session missing",
  "session expired",
  "session not found",
  "invalid claim",
  "no user found",
];

const PERMISSION_ERROR_SIGNALS = [
  "row-level security",
  "row level security",
  "permission denied",
  "insufficient privilege",
  "forbidden",
  "not allowed",
  "access denied",
  "admin access required",
];

const NOT_FOUND_ERROR_SIGNALS = [
  "not found",
  "no rows returned",
  "does not exist",
  "pgrst116",
];

const RATE_LIMIT_ERROR_SIGNALS = [
  "rate limit",
  "too many requests",
  "too many attempts",
  "try again later",
  "429",
];

const PAYMENT_ERROR_SIGNALS = [
  "card_declined",
  "card was declined",
  "your card",
  "insufficient funds",
  "expired_card",
  "incorrect_cvc",
  "payment failed",
  "payment_intent",
];

/**
 * The action cannot succeed no matter how many times it is tried, because it has already happened.
 * These get their own sentence because telling a member to "try again" here would waste their time.
 */
const ALREADY_DONE_ERROR_SIGNALS = [
  "duplicate key",
  "already exists",
  "unique constraint",
  "23505",
  "already been claimed",
  "already claimed",
  "already checked in",
  "already applied",
  "already registered",
  "already a member",
  "already sent",
  "already cancelled",
];

/** The event is full. "Try again" is the wrong advice, so this gets its own sentence too. */
const CAPACITY_ERROR_SIGNALS = [
  "at capacity",
  "sold out",
  "no tickets available",
  "waitlist",
];

const TOO_LARGE_ERROR_SIGNALS = [
  "payload too large",
  "entity too large",
  "file too large",
  "exceeds the maximum",
  "413",
];

/** Pull whatever text we can out of anything that was thrown, without ever showing it to a member. */
function rawTextOf(err: unknown): string {
  if (!err) return "";
  if (typeof err === "string") return err;
  if (err instanceof Error) return `${err.name} ${err.message}`;
  if (typeof err === "object") {
    const candidate = err as {
      message?: unknown;
      error?: unknown;
      error_description?: unknown;
      statusText?: unknown;
      code?: unknown;
      status?: unknown;
      name?: unknown;
    };
    return [
      candidate.name,
      candidate.message,
      typeof candidate.error === "string" ? candidate.error : undefined,
      candidate.error_description,
      candidate.statusText,
      candidate.code,
      candidate.status,
    ]
      .filter((part) => typeof part === "string" || typeof part === "number")
      .join(" ");
  }
  return "";
}

function matches(text: string, signals: string[]): boolean {
  return signals.some((signal) => text.includes(signal));
}

/** Numeric HTTP status, if the thrown thing carried one. */
function statusOf(err: unknown): number | null {
  if (err && typeof err === "object") {
    const candidate = err as { status?: unknown; statusCode?: unknown };
    const raw = candidate.status ?? candidate.statusCode;
    if (typeof raw === "number") return raw;
    if (typeof raw === "string" && /^\d+$/.test(raw)) return Number(raw);
  }
  return null;
}

/**
 * True when the request never reached us: the member is offline, on a bad signal, or something in
 * between blocked it. Use this instead of comparing against the browser's own wording by hand.
 */
export function isNetworkError(err: unknown): boolean {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  const text = rawTextOf(err).toLowerCase();
  if (!text) return false;
  // A bare TypeError from fetch is the browser's way of saying "that request never left the device".
  if (err instanceof TypeError && text.includes("fetch")) return true;
  return matches(text, NETWORK_ERROR_SIGNALS);
}

/**
 * Turn any thrown thing into a sentence a member can act on.
 *
 * @param err       whatever was caught. Never shown to the member.
 * @param fallback  what to say when we cannot tell what went wrong. Make it describe what the member
 *                  was trying to do, for example "We could not save your changes just now. Please try again."
 * @param translate optional. Turns a recognised-cause key into the member's own language (from the
 *                  "errors" namespace). When omitted we use the English source. On the client, prefer
 *                  the useFriendlyError() hook, which wires this up for you.
 */
export function friendlyError(
  err: unknown,
  fallback: string = GENERIC_ERROR_MESSAGE,
  translate?: FriendlyErrorTranslator,
): string {
  // Developers get the truth. Members get their own language (or English if we have no translator).
  if (err !== undefined && err !== null && typeof console !== "undefined") {
    console.error("[friendlyError] original error:", err);
  }

  const say = (key: FriendlyErrorKey): string => (translate ? translate(key) : FRIENDLY_ERROR_EN[key]);

  const text = rawTextOf(err).toLowerCase();
  const status = statusOf(err);

  if (isNetworkError(err)) {
    return say("friendlyOffline");
  }

  if (status === 401 || status === 403 || matches(text, AUTH_ERROR_SIGNALS)) {
    // 403 with a permissions flavour is a different sentence to a signed-out 401.
    if (status !== 401 && matches(text, PERMISSION_ERROR_SIGNALS)) {
      return say("friendlyNoPermission");
    }
    return say("friendlySessionExpired");
  }

  if (matches(text, PERMISSION_ERROR_SIGNALS)) {
    return say("friendlyNoPermission");
  }

  if (status === 429 || matches(text, RATE_LIMIT_ERROR_SIGNALS)) {
    return say("friendlyRateLimited");
  }

  if (matches(text, PAYMENT_ERROR_SIGNALS)) {
    return say("friendlyPayment");
  }

  if (matches(text, CAPACITY_ERROR_SIGNALS)) {
    return say("friendlyEventFull");
  }

  if (matches(text, ALREADY_DONE_ERROR_SIGNALS)) {
    return say("friendlyAlreadyDone");
  }

  if (matches(text, TOO_LARGE_ERROR_SIGNALS)) {
    return say("friendlyFileTooLarge");
  }

  if (status === 404 || matches(text, NOT_FOUND_ERROR_SIGNALS)) {
    return say("friendlyNotFound");
  }

  return fallback;
}
